import {
  buildGoalsById,
  canGoalHaveChildren,
  getGoalLifeAreas,
} from '@griply/utils';
import { useResolveGoalParentChanges } from 'features/goals';
import { useSortedLocalizedLifeAreas } from 'features/life-areas';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GoalCardMultiSelectContextMenu } from 'shared/components/connected/goal-card-multi-select-context-menu';
import { TreeSectionHeader } from 'shared/components/ui/tree-section-header';
import {
  RenderItemProps,
  rootId,
  TaskListItemTypes,
  TreeItem,
  TreeItemMove,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { Goal, GoalLifeAreaLinkChange, UpdateGoal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { InheritColors } from 'shared/types/inherit-colors';
import { LifeArea } from 'shared/types/life-area';
import { lifeAreaOtherID } from 'shared/types/orderable-section';

import { ARCHIVED_SECTION_ID, COMPLETED_SECTION_ID } from '../../constants';
import { useContextMenu } from '../../context-menu-context';
import { useLifeAreaGoalListTree } from '../../hooks/use-life-area-goal-list-tree';
import { useOpenCreateInList } from '../../hooks/use-open-create-in-list';
import { GroupingGoalListProps } from '../../types';
import { TreeGoalCard } from '../tree-goal-card';
import * as Styled from './life-area-grouping-goal-list.style';

const droppedTypes = {
  [TaskListItemTypes.Root]: [
    TaskListItemTypes.Goal,
    TaskListItemTypes.LifeArea,
  ],
  [TaskListItemTypes.Goal]: [TaskListItemTypes.Goal],
  [TaskListItemTypes.GoalDeadline]: [],
  [TaskListItemTypes.Task]: [],
  [TaskListItemTypes.LifeArea]: [TaskListItemTypes.Goal],
  [TaskListItemTypes.Priority]: [],
  [TaskListItemTypes.Date]: [],
  [TaskListItemTypes.DayPart]: [],
  [TaskListItemTypes.Tag]: [],
  [TaskListItemTypes.Type]: [],
  [TaskListItemTypes.NewTask]: [],
  [TaskListItemTypes.Section]: [],
};

const dragTypes = [TaskListItemTypes.Goal];

export const LifeAreaGroupingGoalList: React.FC<GroupingGoalListProps> = ({
  goals,
  goalChildrenByParentId,
  collapsedGoals,
  onGoalCollapse,
  scrollElement,
  onReorder,
  onUpdateProgress,
  hideArchived,
  hideCompleted,
  rootLink,
  showParentGoalLabel,
  showParentLifeAreaLabel,
  showParentLabelsForGoals,
  isProjectList,
  isMultiSelectModifierPressed,
  excludedContextMenuOptions,
}) => {
  const { t } = useTranslation();
  const lifeAreas = useSortedLocalizedLifeAreas();

  const links = useEntityLinks();
  const resolveGoalParentChanges = useResolveGoalParentChanges();

  const goalsMap = useMemo(
    () =>
      goals.reduce<Record<ID, Goal>>((acc, goal) => {
        acc[goal.id] = goal;
        return acc;
      }, {}),
    [goals],
  );

  const goalsById = useMemo(() => buildGoalsById(goals), [goals]);

  const lifeAreasMap = useMemo(
    () =>
      lifeAreas.reduce<Record<ID, LifeArea>>((acc, lifeArea) => {
        acc[lifeArea.id] = lifeArea;
        return acc;
      }, {}),
    [lifeAreas],
  );

  const treeItems = useLifeAreaGoalListTree(goals, lifeAreas, {
    hideArchived,
    hideCompleted,
    goalChildrenByParentId,
  });

  const _onReorder = (updatedItems: TreeItem[], moves: TreeItemMove[]) => {
    if (!onReorder) {
      return;
    }

    const updatedDocs: UpdateGoal[] = [];
    const updatedSections: { id: ID; goalSorting: ID[] }[] = [];

    // A goal can render under several life areas, so the drop's from/to parents
    // decide which single link moves — the rest of its set stays put.
    const goalLinkChanges = moves.reduce<GoalLifeAreaLinkChange[]>(
      (acc, { itemId, fromParentId, toParentId }) => {
        const goal = goalsMap[itemId];
        if (!goal || toParentId === fromParentId) {
          return acc;
        }

        const droppedOnGoal = !!goalsMap[toParentId];
        const currentIds = getGoalLifeAreas(goal, { goalsById, links });
        const remaining = currentIds.filter((id) => id !== fromParentId);

        if (droppedOnGoal) {
          acc.push({ goalId: itemId, lifeAreaIds: remaining });
          return acc;
        }

        const targetId =
          toParentId === lifeAreaOtherID || toParentId === rootId
            ? undefined
            : toParentId;

        acc.push({
          goalId: itemId,
          lifeAreaIds: targetId
            ? Array.from(new Set([...remaining, targetId]))
            : remaining,
        });
        return acc;
      },
      [],
    );

    // Sections and goal folders own an order; only a goal that changed container gets parent fields.
    const movesById = new Map(
      moves
        .filter(({ fromParentId, toParentId }) => fromParentId !== toParentId)
        .map((move) => [move.itemId, move]),
    );
    const goalParentChanges = resolveGoalParentChanges(moves, [
      rootId,
      lifeAreaOtherID,
      COMPLETED_SECTION_ID,
      ARCHIVED_SECTION_ID,
      ...lifeAreas.map(({ id }) => id),
    ]);

    updatedItems.forEach((item) => {
      if (item.type === TaskListItemTypes.Goal) {
        const goal = goalsMap[item.id];
        if (!goal) {
          return;
        }
        const goalToUpdate: UpdateGoal = {
          id: item.id,
          goalSorting: item.children ?? null,
        };

        const move = movesById.get(item.id);
        const targetGoal = move ? goalsMap[move.toParentId] : undefined;
        const pickedFromGoal =
          !!move?.fromParentId && !!goalsMap[move.fromParentId];

        if (targetGoal) {
          goalToUpdate.ancestorIds = [
            ...(targetGoal.ancestorIds ?? []),
            targetGoal.id,
          ];
          goalToUpdate.color =
            goal.color === InheritColors.LifeArea
              ? InheritColors.Goal
              : goal.color;
        }

        if (move && !targetGoal && pickedFromGoal) {
          goalToUpdate.ancestorIds = null;
          goalToUpdate.color =
            goal.color === InheritColors.Goal
              ? InheritColors.LifeArea
              : goal.color;
        }

        updatedDocs.push(goalToUpdate);
      }

      if (item.type === TaskListItemTypes.LifeArea) {
        updatedSections.push({ id: item.id, goalSorting: item.children ?? [] });
      }

      if (item.type === TaskListItemTypes.Root) {
        updatedSections.push({
          id: rootId,
          goalSorting: item.children ?? [],
        });
      }
    });

    onReorder(updatedSections, updatedDocs, goalLinkChanges, goalParentChanges);
  };

  // Projects stay leaves: a drop onto one would make it the parent.
  const canDropOnItem = useCallback(
    (target: TreeItem) => canGoalHaveChildren(goalsMap[target.id]),
    [goalsMap],
  );

  const { close, position, payload } = useContextMenu();
  const openCreateInList = useOpenCreateInList(isProjectList);

  const renderItem = useCallback(
    ({ item, itemInstance, tree }: RenderItemProps) => {
      const itemId = itemInstance.getItemData().sourceId;
      const itemData = treeItems[itemId];

      if (!itemData) {
        return null;
      }

      if (itemData.type === TaskListItemTypes.Goal) {
        const goal = goalsMap[itemId];
        if (!goal) {
          return null;
        }

        return (
          <TreeGoalCard
            rootLink={rootLink}
            sectionsAreLifeAreas
            item={itemInstance}
            goal={goal}
            tree={tree}
            onUpdateProgress={onUpdateProgress}
            hideLifeArea
            showParentGoalLabel={showParentGoalLabel}
            showParentLifeAreaLabel={showParentLifeAreaLabel}
            showParentLabelsForGoals={showParentLabelsForGoals}
            isMultiSelectModifierPressed={isMultiSelectModifierPressed}
            excludedContextMenuOptions={excludedContextMenuOptions}
          />
        );
      }

      if (item.type === TaskListItemTypes.LifeArea) {
        const rootChildren = treeItems.root.children ?? [];
        const isFirst = rootChildren[0] === itemId;

        if (itemId === COMPLETED_SECTION_ID) {
          return (
            <TreeSectionHeader
              item={itemInstance}
              lifeArea={undefined}
              overrideTitle={t('pages.goals.completed.title')}
              overrideIcon={null}
              disableLink
              useSecondaryTitle
              isFirst={isFirst}
            />
          );
        }
        if (itemId === ARCHIVED_SECTION_ID) {
          return (
            <TreeSectionHeader
              item={itemInstance}
              lifeArea={undefined}
              overrideTitle={t('pages.goals.archived.title')}
              overrideIcon={null}
              disableLink
              useSecondaryTitle
              isFirst={isFirst}
            />
          );
        }

        const lifeArea = lifeAreasMap[itemId];
        const handleAddGoal = () =>
          openCreateInList({
            lifeAreaId: lifeArea?.id,
          });

        return (
          <TreeSectionHeader
            item={itemInstance}
            lifeArea={lifeArea}
            isFirst={isFirst}
            onAddGoal={handleAddGoal}
          />
        );
      }

      return null;
    },
    [
      treeItems,
      rootLink,
      goalsMap,
      onUpdateProgress,
      isMultiSelectModifierPressed,
      excludedContextMenuOptions,
      showParentGoalLabel,
      showParentLifeAreaLabel,
      showParentLabelsForGoals,
      lifeAreasMap,
      t,
      openCreateInList,
    ],
  );

  if (!goals.length) {
    return null;
  }

  return (
    <Styled.Container>
      <VirtualizedTreeList
        items={treeItems}
        rootItemId={rootId}
        renderItem={renderItem}
        scrollElement={scrollElement}
        collapsedItems={collapsedGoals}
        onCollapse={onGoalCollapse}
        onReorder={_onReorder}
        canDropOnItem={canDropOnItem}
        dropTypes={droppedTypes}
        dragTypes={dragTypes}
        indentationWidth={2.5}
      />
      {position && payload && (
        <GoalCardMultiSelectContextMenu
          goals={payload.goals}
          location={position}
          onClose={close}
          initialActiveSubMenu={payload.initialActiveSubMenu}
        />
      )}
    </Styled.Container>
  );
};
